import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './OffersPage.module.css';
import offerService from '../services/offerService';
import { OfferModel } from '../models/OfferModel';
import { formatPrice } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import OfferTermsComp from '../components/OfferTermsComp';
import PaginationComp from '../components/PaginationComp';

const OffersPage = () => {

  const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;

  const [offers, setOffers] = useState<OfferModel[]>([]);
  const [pageNum, setPageNum] = useState(0);
  const [totalOffers, setTotalOffers] = useState(0);
  const pageSize = 10;
  const [keywords, setKeywords] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [serviceError, setServiceError] = useState('');
  const [statusList] = useState(['Created', 'Processing', 'Completed', 'Canceled']);
  const [stateFilter, setStateFilter] = useState<string>("");
  const [hasResetFilter, setHasResetFilter] = useState(false);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      searchOffer();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [pageNum, keywords, stateFilter]);



  const searchOffer = async () => {
    setIsLoading(true);
    try {
      const response: { offers: OfferModel[], offerCount: number } = await offerService.searchOffers(pageNum * pageSize, pageSize, keywords, stateFilter);
      setOffers(response.offers);
      setTotalOffers(response.offerCount);
    } catch (error) {
      setServiceError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
      setHasResetFilter(false);
    }
  }




  const changePage = (newPageNum: number) => {
    setPageNum(newPageNum);
  };

  const getStateBadgeColor = (state: string) => {
    if (state === "Processing")
      return "bg-danger";
    if (state === "Completed")
      return "bg-success";
    if (state === "Canceled")
      return "bg-secondary";
    return "bg-primary";
  }

  const handleResetFilter = () => {
    setStateFilter('');
    setKeywords('');
    setPageNum(0);
    setHasResetFilter(true);
    //searchOffer(); // Reset everything and reload
  }


  const updateTermsAccepted = async (offer: OfferModel) => {
    try {
      const result = await offerService.updateOfferTermsAccepted(offer.offerId);
      if (result === "Success") {
        setOffers(prevOffers => prevOffers.map(o => o.offerId === offer.offerId ? { ...o, termsAccepted: "Yes" } : o));
      }
    } catch (error: any) {
      setServiceError(error?.message || 'Failed to accept offer terms. Please try again later.');
    }
  }

  return (
    <div className='mt-3'>
      <div className='row mb-5'>
        <div className='col-12'>
          <div className="card rounded-0">
            <div className="card-body p-4">
              <h5>Offers</h5>
              <div className='row mt-3 mb-1'>
                <div className="col-md-9">
                  <div className="input-group mt-2">
                    <span className={`input-group-text ${styles.offerSearchIcon}`}>
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className={`form-control ${styles.offerSearchInput}`}
                      placeholder="Search by keywords"
                      aria-label="Search items"
                      aria-describedby="search-items"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                    />
                  </div>
                </div>
                {/* Filter */}
                <div className="col-md-3 d-flex gap-4 mt-2">
                  <select
                    className="form-select"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                  >
                    <option value="">Filter by Status</option>
                    {statusList.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <button type="button" title='Reset Filter' className="btn btn-outline-dark rounded-0" onClick={handleResetFilter}>Reset</button>
                </div>
              </div>

            </div>
          </div>
        </div>
        {serviceError && <div className="col-md-12 mt-3 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}
        <div className="col-md-12 mt-3">
          {isLoading && (
            <div className="text-center gap-2 mt-3 text-secondary">
              <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
              <span>  Searching...</span>
            </div>
          )}
          {/* Empty results */}
          {!isLoading && offers.length === 0 && (
            (keywords !== '' || stateFilter !== '') ? (
              <div className="border fs-4 mt-4 p-5 text-center">
                <i className="bi bi-stars clearfix"></i>
                <span className='clearfix'>There are currently no offers matching your search criteria.</span>
                <span>Try different keywords or filter.</span>
              </div>
            ) : (
              !hasResetFilter && (<div className="border fs-4 mt-4 p-5 text-center">
                <i className="bi bi-stars clearfix"></i>
                <span className='clearfix'>There are currently no offers.</span>
              </div>)
            )
          )}
          {/* offers list */}
          {!isLoading && offers.map(offer => (
            <div key={offer.offerId} className="card mb-3 rounded-0 p-4">
              {offer.termsAccepted === "" ?
                <div className="row">
                  <div className='col-md-6 fw-bold'>ID: #{offer.offerId}</div>
                  <div className="mb-3 mt-3">

                    <OfferTermsComp
                      offer={offer}
                      onAccept={updateTermsAccepted}
                    />



                  </div>
                </div>
                :
                <div className="row">
                  <div className='col-md-6 fw-bold'>ID: #{offer.offerId}</div>
                  <div className='col-md-6 text-end'><span className={`badge ${getStateBadgeColor(offer.state)} rounded-0`}>{offer.state}</span></div>
                  <div className={`col-md-12 mt-3 mb-3`}>
                    <div className={`${styles.offerItem}`}>
                      <img className={`float-start border ${styles.offerItemImage}`} src={offer.itemImage ? `${itemImageURL}/${offer.itemImage}` : "/images/fallback.png"}></img>
                      <div className={`${styles.offerItemInfo}`}>
                        <p className='one-line-text-ellipsis'>{offer.itemName}</p>
                        <div className="row">
                          <div className={`col-md-2 offset-md-8 ${styles.offerQty}`}>Quantity: {offer.quantity}</div>
                          <div className="col-md-2 text-end">Price: {formatPrice(offer.itemPrice)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`col-md-6 ${styles.offerCreatedItem}`}>{formatDate(offer.createdTime)}</div>
                  <div className='col-md-6 text-danger text-end'><b><span className='pe-3'>Amount:</span>{formatPrice(offer.amount)}</b></div>
                  <div className="col-md-12 mt-3 text-end">
                    <Link to={`/offers/view/${offer.offerId}`} state={{ offer }} className="btn btn-sm btn-outline-dark rounded-0" style={{ minWidth: '80px' }}>View</Link>
                  </div>
                  {serviceError && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}
                </div>
              }
            </div>
          ))}
        </div>

        {/* Pagination */}
        {!isLoading && offers.length > 0 && (
          <PaginationComp pageNum={pageNum} pageSize={pageSize} totalCount={totalOffers} onPageChange={changePage} />
        )}
      </div>
    </div>
  );
};

export default OffersPage;
